// The press on a commission mark (ui-map-interface.md §6.7, R2645, R2988).
//
// The mark is a pennant at the target cell of a commission
// (`render/commission-mark.ts`). A press on it opens the card of the
// commission in FLEET · COMMISSIONS. The press rides the chart mark path
// (`Commands.markAt`, `Commands.pressMark`), so it takes the same place in
// the order of a press: the aim, an armed pick mode, a hull and a site
// take the press first, and the pennant comes before the chart furniture.
// The mark id is `commission:<id>`, and `onMark` of `fast/main.ts` reads
// it back with `commissionOfMark`.

import { COMMISSION_MARK } from '../gen/presentation';
import type { Commission } from '../gen/proto/aquaphobia';
import { commissionBox } from '../render/commission-mark';
import { cellCenter } from '../world';

/** The prefix of the mark id of a commission. */
const COMMISSION_MARK_ID = 'commission:';

/** The mark id of commission `id`. */
export function commissionMarkId(id: number): string {
  return `${COMMISSION_MARK_ID}${id}`;
}

/** The commission id of a mark id, or 0 when the mark is no commission. */
export function commissionOfMark(markId: string): number {
  if (!markId.startsWith(COMMISSION_MARK_ID)) return 0;
  const id = Number(markId.slice(COMMISSION_MARK_ID.length));
  return Number.isInteger(id) && id > 0 ? id : 0;
}

/**
 * The commission whose pennant is nearest to a world point, within `tol`
 * world px of the box of the pennant, or null. The box is the baked cell
 * of the mark around the foot of the staff, so a press on the flag and a
 * press on the staff both count. The marker layer draws the first
 * `maxMarks` of the list, so the pick reads no more than those.
 */
export function commissionAt(
  list: readonly Commission[], wx: number, wy: number, tol: number,
): Commission | null {
  const b = commissionBox();
  const n = Math.min(list.length, COMMISSION_MARK.maxMarks);
  let best: Commission | null = null;
  let bestD = tol;
  for (let i = 0; i < n; i++) {
    const [x, y] = cellCenter(list[i].cell);
    const dx = Math.max(x + b.x - wx, 0, wx - (x + b.x + b.w));
    const dy = Math.max(y + b.y - wy, 0, wy - (y + b.y + b.h));
    const d = Math.hypot(dx, dy);
    if (d <= bestD) { best = list[i]; bestD = d; }
  }
  return best;
}
